<script setup lang="ts">
// One setting row (contract 22 §10) — the v4 form of v3's `Workspace/CustomRow.vue`, the module's
// highest-leverage export at 29 importers. Four deliberate changes from the legacy:
//
//   * `<tr>/<td>` → a self-contained grid, so a row no longer needs a `<table>` ancestor and a
//     registered pane in 30 or 23 can lay its rows out however it likes;
//   * `title`/`tooltip` STRINGS → `titleKey`/`tooltipKey`, so no caller can pass raw copy (§13).
//     `titleKey` is required, which retires the `title=" "` spacer hack at
//     `WorkspaceSettingsArea.vue:84` — a layout row is not a setting row;
//   * `isOnboard` (declared at `CustomRow.vue:31`, never read) and the `buttons` slot (`:18` —
//     zero consumers across all 29 call sites) are dropped rather than ported;
//   * Basics can opt into the legacy icon-only edit affordance; all other callers retain the
//     labelled action. The icon branch carries a translated accessible name and Tooltip through
//     shared primitives instead of relying on a vendor glyph class.
//
// `disabled` + `disabledReasonKey` ARE §12's degraded render — a viewer sees the row and its value
// with the action disabled and the reason on hover, never a redirect and never a blank pane.
import { useI18n } from '@/shared/i18n'
import { Button, Icon, PlatformLogo, resolveStaticPlatformLogo, Tooltip } from '@/shared/ui'

withDefaults(
  defineProps<{
    /** i18n key for the row label — a key, never a literal (§13). */
    titleKey: string
    /** i18n key for the label tooltip. Omitted = the label carries no tooltip at all. */
    tooltipKey?: string
    /** 02 `PlatformLogo` platform token, rendered before the label. */
    logo?: string
    /** Drop the action entirely — the row is read-only by nature, not by permission. */
    hideAction?: boolean
    /** Render the action disabled: pending write, or the RBAC-degraded state (§12). */
    disabled?: boolean
    /** i18n key explaining WHY the action is disabled; surfaced as a tooltip on it. */
    disabledReasonKey?: string
    /** i18n key for the action's own label. Defaults to "Edit"; a pane overrides it when the verb
     *  changes with the row's state — Notifications shows "Set Up" until a channel has recipients
     *  (`NotificationCard.vue:30`). Still a key, never a literal (§13). */
    actionKey?: string
    /** Use the v3 Basics pen-to-square action without changing labelled actions in other panes. */
    iconAction?: boolean
    /** With `hideAction`: keep the icon action's column width, so a header row's value columns
     *  line up with the editable rows under it (v3's `<table>` did this by construction). */
    reserveAction?: boolean
    /** Keep the label for assistive tech only — a continuation row under a header row (v3
     *  `KeyEvents.vue`'s per-account rows leave the label cell empty). */
    titleHidden?: boolean
  }>(),
  {
    hideAction: false,
    disabled: false,
    actionKey: 'workspaceSettings.common.edit',
    iconAction: false,
    reserveAction: false,
    titleHidden: false,
  },
)

const emit = defineEmits<{ edit: [] }>()
const { t } = useI18n()

function platformLogoProps(logo: string | undefined): { src: string } | Record<string, never> {
  const src = resolveStaticPlatformLogo(logo)
  return src ? { src } : {}
}
</script>

<template>
  <!-- Both tooltips are OPTIONAL wrappers, not layout: 02's Tooltip opens on hover whatever its
       text, so an always-mounted one would flash an empty panel on every untooltipped row. It is
       also a fragment-root component (trigger + portal), so attributes do NOT fall through to it —
       hence the stable `__label` / `__action` elements around it rather than on it. -->
  <div
    class="setting-row grid grid-cols-[minmax(0,_3fr)_minmax(0,_7fr)_auto] items-center gap-md py-md px-lg border-t border-t-border bg-surface text-fg font-sans max-md:grid-cols-[minmax(0,_1fr)] max-md:justify-items-start max-md:gap-xs"
    data-testid="setting-row"
  >
    <div class="setting-row__label inline-flex min-w-0 items-center gap-2xs">
      <PlatformLogo
        v-if="logo && !titleHidden"
        :platform="logo"
        :size="20"
        v-bind="platformLogoProps(logo)"
      />
      <component
        :is="tooltipKey ? Tooltip : 'span'"
        v-bind="tooltipKey ? { text: t(tooltipKey) } : {}"
      >
        <span
          class="setting-row__title text-[0.875rem] font-medium"
          :class="{ 'sr-only': titleHidden }"
          >{{ t(titleKey) }}</span
        >
      </component>
    </div>

    <div class="setting-row__value min-w-0 text-[0.875rem]"><slot /></div>

    <div
      v-if="!hideAction"
      class="setting-row__action inline-flex justify-end max-md:justify-start"
    >
      <Tooltip
        v-if="iconAction"
        :text="t(disabled && disabledReasonKey ? disabledReasonKey : actionKey)"
      >
        <Button
          variant="soft"
          size="md"
          square
          :disabled="disabled"
          :aria-label="t(actionKey)"
          data-testid="setting-row-edit"
          @click="emit('edit')"
        >
          <Icon name="pen-to-square" :size="16" aria-hidden="true" />
        </Button>
      </Tooltip>
      <component
        :is="disabled && disabledReasonKey ? Tooltip : 'span'"
        v-else
        v-bind="disabled && disabledReasonKey ? { text: t(disabledReasonKey) } : {}"
      >
        <Button
          variant="soft"
          size="sm"
          :disabled="disabled"
          data-testid="setting-row-edit"
          @click="emit('edit')"
        >
          {{ t(actionKey) }}
        </Button>
      </component>
    </div>
    <div
      v-else-if="reserveAction"
      class="setting-row__action inline-flex justify-end max-md:hidden"
      aria-hidden="true"
    >
      <span class="block size-10" />
    </div>
  </div>
</template>
